(() => {
  const normalise = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const optionKey = value => normalise(value).replace(/^color$/, 'colour');
  const replaceTokens = (text, data) => Object.entries(data).reduce((out, [key, value]) => out.replaceAll(`{${key}}`, String(value)), text);

  document.addEventListener('click', event => {
    const shopLink = event.target.closest('a[href="#ProductGridContainer"]');
    if (shopLink) {
      const results = shopLink.closest('.shopify-section')?.querySelector('.rss-finder-results');
      if (results && !results.hidden) {
        event.preventDefault();
        results.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    }
    const guideLink = event.target.closest('[data-rss-open-guide]');
    if (guideLink) {
      const guide = document.getElementById(guideLink.hash.slice(1));
      if (guide) {
        event.preventDefault();
        guide.open = true;
        guide.scrollIntoView({ block: 'center', behavior: 'auto' });
        guide.querySelector('summary')?.focus({ preventScroll: true });
      }
    }
    const optionsLink = event.target.closest('[data-rss-jump-options]');
    if (optionsLink) {
      const scope = document.getElementById(optionsLink.hash.slice(1));
      const control = scope?.querySelector('variant-selects select, variant-radios input:not([disabled])');
      if (control) {
        event.preventDefault();
        control.scrollIntoView({ block: 'center', behavior: 'auto' });
        control.focus({ preventScroll: true });
      }
    }
  });

  if (customElements.get('rss-collection-finder')) return;
  customElements.define('rss-collection-finder', class extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.strings = JSON.parse(this.querySelector('[data-rss-finder-strings]').textContent);
      this.disclosure = this.querySelector('details');
      this.status = this.querySelector('.rss-finder-status');
      this.controls = this.querySelector('[data-rss-finder-controls]');
      this.results = this.querySelector('.rss-finder-results');
      this.grid = this.querySelector('[data-rss-finder-grid]');
      this.resetButton = this.querySelector('[data-rss-finder-reset]');
      this.retryButton = this.querySelector('[data-rss-finder-retry]');
      this.scope = this.closest('.shopify-section') || this.parentElement;
      this.page = 1;
      this.pageSize = 24;
      this.hidden = false;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.disclosure.addEventListener('toggle', () => {
        if (this.disclosure.open) this.load();
      }, options);
      this.resetButton.addEventListener('click', () => this.reset(), options);
      this.retryButton.addEventListener('click', () => this.load(), options);
      this.controls.addEventListener('change', () => { this.page = 1; this.render(); }, options);
      this.controls.addEventListener('input', event => {
        if (event.target.type !== 'search') return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => { this.page = 1; this.render(); }, 160);
      }, options);
      this.querySelector('[data-rss-finder-prev]').addEventListener('click', () => this.turn(-1), options);
      this.querySelector('[data-rss-finder-next]').addEventListener('click', () => this.turn(1), options);
      window.addEventListener('popstate', () => this.invalidate(), options);
      this.observer = new MutationObserver(() => {
        if (this.sourceKey && this.sourceKey !== this.key()) this.invalidate();
      });
      this.observer.observe(this.scope, { childList: true, subtree: true });
    }

    key() {
      const url = new URL(location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('view');
      return url.pathname + url.search;
    }

    async fetchPage(page) {
      const url = new URL(this.dataset.collectionUrl, location.origin);
      const current = new URL(location.href);
      for (const [key, value] of current.searchParams) {
        if (key.startsWith('filter.') || ['sort_by', 'preview_theme_id', '_fd', 'pb'].includes(key)) url.searchParams.append(key, value);
      }
      url.searchParams.set('view', 'rss-finder');
      url.searchParams.set('page', String(page));
      const response = await fetch(url, { credentials: 'same-origin', signal: this.fetchController.signal });
      if (!response.ok) throw new Error('Could not load collection');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const wrapper = parsed.querySelector('[data-rss-finder-pages]');
      if (!wrapper) throw new Error('Collection data unavailable');
      return {
        pages: Number(wrapper.dataset.rssFinderPages),
        items: [...wrapper.querySelectorAll('template[data-rss-finder-item]')].map(template => {
          const data = JSON.parse(template.dataset.rssFinderItem);
          const attributes = new Map();
          for (const option of data.options) {
            const key = optionKey(option.name);
            if (key === 'title' || key === 'option') continue;
            attributes.set(key, option.values.map(String));
          }
          if (data.facts.species) attributes.set('species', [data.facts.species]);
          if (data.facts.pack) attributes.set('pack', [data.facts.pack]);
          if (data.facts.length && data.facts.group === 'firewood') attributes.set('length', [data.facts.length]);
          return { ...data, attributes, template };
        })
      };
    }

    async load() {
      if (this.loading || this.items) return;
      this.loading = true;
      this.sourceKey = this.key();
      const source = this.sourceKey;
      this.fetchController = new AbortController();
      this.status.textContent = this.strings.loading;
      this.retryButton.hidden = true;
      this.disclosure.setAttribute('aria-busy', 'true');
      try {
        const first = await this.fetchPage(1);
        const results = [first];
        let next = 2;
        const worker = async () => {
          while (next <= first.pages) {
            const page = next++;
            results[page - 1] = await this.fetchPage(page);
          }
        };
        await Promise.all(Array.from({ length: Math.min(3, Math.max(0, first.pages - 1)) }, worker));
        if (source !== this.key()) return;
        const seen = new Set();
        this.items = results.flatMap(result => result.items).filter(item => {
          if (seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        });
        this.buildControls();
        this.status.textContent = this.strings.ready;
        this.resetButton.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.status.textContent = this.strings.error;
          this.retryButton.hidden = false;
          this.restoreOriginal();
        }
      } finally {
        this.loading = false;
        this.disclosure.removeAttribute('aria-busy');
      }
    }

    makeLabel(text, control) {
      control.setAttribute('aria-label', text);
      const label = document.createElement('label');
      const title = document.createElement('span');
      title.textContent = text;
      label.append(title, control);
      return label;
    }

    buildControls() {
      this.controls.replaceChildren();
      this.search = document.createElement('input');
      this.search.type = 'search';
      this.search.placeholder = this.strings.search_placeholder;
      this.controls.append(this.makeLabel(this.strings.search, this.search));
      const facets = new Map();
      this.items.forEach(item => item.attributes.forEach((values, key) => {
        if (!facets.has(key)) facets.set(key, new Set());
        values.forEach(value => facets.get(key).add(value));
      }));
      const priority = ['species', 'pack', 'material', 'size', 'dimensions', 'colour', 'roll width', 'length', 'quantity'];
      this.selects = [];
      [...facets].filter(([, values]) => values.size > 1).sort(([a], [b]) => {
        const score = key => priority.includes(key) ? priority.indexOf(key) : 99;
        return score(a) - score(b);
      }).slice(0, 8).forEach(([key, values]) => {
        const select = document.createElement('select');
        select.dataset.facet = key;
        select.append(new Option(this.strings.all, ''));
        [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).forEach(value => select.append(new Option(value, value)));
        this.selects.push(select);
        const label = this.strings[key] || key.charAt(0).toUpperCase() + key.slice(1);
        this.controls.append(this.makeLabel(label, select));
      });
      this.sort = document.createElement('select');
      [['featured', 'featured'], ['price_asc', 'price_asc'], ['price_desc', 'price_desc'], ['name_asc', 'name_asc']].forEach(([value, key]) => this.sort.append(new Option(this.strings[key], value)));
      this.controls.append(this.makeLabel(this.strings.sort, this.sort));
      this.stock = document.createElement('input');
      this.stock.type = 'checkbox';
      const stockLabel = this.makeLabel(this.strings.available, this.stock);
      stockLabel.className = 'rss-finder-stock';
      this.controls.append(stockLabel);
    }

    matches(item, excluded) {
      const query = normalise(this.search.value);
      if (query && !normalise(item.title).includes(query)) return false;
      if (this.stock.checked && !item.available) return false;
      return this.selects.every(select => !select.value || select.dataset.facet === excluded || item.attributes.get(select.dataset.facet)?.includes(select.value));
    }

    render() {
      if (!this.items) return;
      const hasChoice = this.search.value.trim() || this.stock.checked || this.selects.some(select => select.value) || this.sort.value !== 'featured';
      if (!hasChoice) { this.restoreOriginal(); return; }
      const matches = this.items.filter(item => this.matches(item));
      if (this.sort.value === 'price_asc') matches.sort((a, b) => a.price - b.price);
      if (this.sort.value === 'price_desc') matches.sort((a, b) => b.price - a.price);
      if (this.sort.value === 'name_asc') matches.sort((a, b) => a.title.localeCompare(b.title));
      const pages = Math.max(1, Math.ceil(matches.length / this.pageSize));
      this.page = Math.min(pages, this.page);
      this.grid.replaceChildren(...matches.slice((this.page - 1) * this.pageSize, this.page * this.pageSize).map(item => item.template.content.firstElementChild.cloneNode(true)));
      this.results.hidden = false;
      this.native = this.scope.querySelector('facet-filters-form');
      if (this.native) this.native.hidden = true;
      this.querySelector('[data-rss-finder-count]').textContent = matches.length ? replaceTokens(this.strings.results, { count: matches.length, total: this.items.length }) : this.strings.empty;
      this.querySelector('[data-rss-finder-page]').textContent = replaceTokens(this.strings.page, { page: this.page, pages });
      this.querySelector('[data-rss-finder-prev]').disabled = this.page <= 1;
      this.querySelector('[data-rss-finder-next]').disabled = this.page >= pages;
      this.querySelector('.rss-finder-pagination').hidden = pages <= 1;
      this.selects.forEach(select => {
        const available = new Set(this.items.filter(item => this.matches(item, select.dataset.facet)).flatMap(item => item.attributes.get(select.dataset.facet) || []));
        [...select.options].forEach(option => { option.disabled = !!option.value && option.value !== select.value && !available.has(option.value); });
      });
    }

    restoreOriginal() {
      this.results.hidden = true;
      this.grid.replaceChildren();
      this.scope.querySelector('facet-filters-form')?.removeAttribute('hidden');
    }

    reset() {
      if (this.search) this.search.value = '';
      if (this.stock) this.stock.checked = false;
      if (this.sort) this.sort.value = 'featured';
      this.selects?.forEach(select => { select.value = ''; [...select.options].forEach(option => { option.disabled = false; }); });
      this.page = 1;
      this.restoreOriginal();
      this.status.textContent = this.strings.ready;
    }

    turn(direction) {
      this.page += direction;
      this.render();
      const count = this.querySelector('[data-rss-finder-count]');
      count.setAttribute('tabindex', '-1');
      count.scrollIntoView({ block: 'center', behavior: 'auto' });
      count.focus({ preventScroll: true });
    }

    invalidate() {
      this.fetchController?.abort();
      this.items = null;
      this.sourceKey = null;
      this.loading = false;
      this.controls.replaceChildren();
      this.resetButton.hidden = true;
      this.restoreOriginal();
      if (this.disclosure.open) this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.fetchController?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      this.scope?.querySelector('facet-filters-form')?.removeAttribute('hidden');
      this.initialised = false;
    }
  });
})();
